/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useCallback, useContext, FunctionComponent } from 'react';
import { SIMULATOR_APP_ID } from '../../constants/Apps';
import { callApi, callAuth } from '../../fetch/fetch';
import { AuthContextTrace } from '../Authenticate/AuthenticateTrace';
import { URLStateContext } from '../URLState/URLState';

interface Options {
  useDemo?: boolean;
}

type ApiContextType = {
  get: (url: string, opts?: Options) => Promise<any>;
  put: (url: string, body?: Record<string, any>) => Promise<any>;
  deleteAuth: (url: string) => Promise<any>;
  getAuth: (url: string) => Promise<any>;
  postAuth: (url: string, body?: Record<string, any>) => Promise<any>;
};

export const ApiContext = createContext<ApiContextType>({
  get: () => Promise.reject(),
  put: () => Promise.reject(),
  deleteAuth: () => Promise.reject(),
  getAuth: () => Promise.reject(),
  postAuth: () => Promise.reject(),
});

const Api: FunctionComponent = ({ children }) => {
  const { token: traceToken } = useContext(AuthContextTrace);
  const { demo } = useContext(URLStateContext);

  const get = useCallback(
    (url: string) =>
      callApi('GET', url, traceToken, undefined, {
        headers: {
          ...(demo && {
            'x-simulated-appid': SIMULATOR_APP_ID,
          }),
        },
      }),
    [demo, traceToken],
  );

  const put = useCallback(
    (url: string, body?: Record<string, unknown>) =>
      callApi('PUT', url, traceToken, body, {
        headers: {
          ...(demo && {
            'x-simulated-appid': SIMULATOR_APP_ID,
          }),
        },
      }),
    [demo, traceToken],
  );

  const deleteAuth = (url: string) => callAuth('DELETE', url, traceToken);
  const getAuth = (url: string) => callAuth('GET', url, traceToken);
  const postAuth = (url: string, body?: Record<string, unknown>) =>
    callAuth('POST', url, traceToken, body);

  return (
    <ApiContext.Provider
      value={{
        get,
        put,
        deleteAuth,
        getAuth,
        postAuth,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export default Api;
